import { isDefined } from '@sgme/fp';
import { SecondaryRealtimeBackend, secondaryStreamingApiConnector } from 'apiConnector/secondary-market/streaming';
import { EMPTY, interval, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  secondaryMarketPriceKeepAliveFailed,
  secondaryMarketPriceKeptAlive,
  secondaryMarketPriceReceived,
} from 'state/secondaryMarket/realtime/realtime.action';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import type { EpicWithThunk } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import { getSecondaryMarketPriceMessage$ } from 'utils/streaming/messages';

export type KeepAliveProductIdsSelector = (state: AppState) => Set<string>;

export function keepAliveStreamingEpic(
  backend: SecondaryRealtimeBackend,
  productIdsSelectors: KeepAliveProductIdsSelector[],
  pollingFrequency: number = 10_000,
): EpicWithThunk {
  return (_action$, state$, { httpClient, scheduler }) => {
    const { keepAlive } = secondaryStreamingApiConnector(httpClient);
    return interval(pollingFrequency, scheduler).pipe(
      withLatestFrom(state$),
      mergeMap(([, state]) => {
        const productIds = [...new Set(productIdsSelectors.flatMap(selector => [...selector(state)]))]
          .map(id => toBcsProductKey(id))
          .filter(isDefined) as BcsProductKey[];

        if (productIds.length === 0) {
          return EMPTY;
        }
        return keepAlive({ productIds, backend }).pipe(
          map(() => secondaryMarketPriceKeptAlive(productIds)),
          catchError(() => of(secondaryMarketPriceKeepAliveFailed(productIds))),
        );
      }),
    );
  };
}

export function updatePriceStreamingEpic(): EpicWithThunk {
  return (_action$, _state$, { streaming }) => {
    return getSecondaryMarketPriceMessage$(streaming).pipe(
      map(event => secondaryMarketPriceReceived(event.bcsProductKey, event)),
    );
  };
}
